import classNames from 'classnames';
import React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useTypedSelector } from '../../../../../hooks';
import { userDataClient } from '../../../../../utils/request';
import { TermsBlock } from './TermsBlock';
import caret from './icon-caret-down.svg';
import { ItemProductSelectionTemp } from '../../../interface';
import { getActiveLanguageSettings } from '../../../../../utils/localization';
import { getPriceStructure } from '../../../ducks';
import { TermsPrice } from './TermsPrice';
import { getWeightRangeForValue } from '@brenger/utils';

const INITIAL_ITEMS_TO_SHOW = 3;

export const TermsBlockItems = (): JSX.Element => {
  const items = useTypedSelector(state => state.generalTransport?.product_selection.items);
  const [showMore, setShowMore] = React.useState(false);
  const showToggleItems = items.length > INITIAL_ITEMS_TO_SHOW;
  const itemsToShow = showMore ? items : items.slice(0, INITIAL_ITEMS_TO_SHOW);
  const priceStructure = useTypedSelector(getPriceStructure);
  const fragile = priceStructure?.global?.find(item => item.charge === 'product_selection.fragile');

  return (
    <div className={'pt-[1em]'}>
      <TermsBlock
        title={<Translate id="request_flow.terms.block_heading.items" />}
        borderRadius="top"
        border="bottom"
        titleClassName="main-title"
      />
      {fragile && (
        <TermsBlock
          title={<Translate id="request_flow.terms.block_heading.fragile" />}
          border="bottom"
          borderRadius="none"
        >
          <div className="flex justify-between">
            <div>
              <Translate id="request_flow.terms.block_heading.fragile_explain" />
            </div>
            <TermsPrice price={fragile.price} />
          </div>
        </TermsBlock>
      )}
      {itemsToShow.map((item, i) => {
        const isLast = i === items.length - 1 && !showToggleItems;
        return (
          <TermsBlock key={i} border={isLast ? 'none' : 'bottom'} borderRadius={isLast ? 'bottom' : 'none'}>
            <div className="flex">
              <div style={{ width: '50px', marginRight: '16px' }}>
                <ItemDisplayImage image={item.job_image} />
              </div>
              <div>
                <div className="font-bold">
                  {item.count} x {item.title}
                </div>
                <div>
                  {item.length} x {item.width} x {item.height} cm{' '}
                  {item.weight_range?.min ? `| ${getWeightRangeForValue(item.weight_range.min).label}` : ''}
                </div>
                <ItemDisplayProductGroupMaterials ps={item.ps_temp} />
              </div>
            </div>
          </TermsBlock>
        );
      })}
      {showToggleItems && (
        <TermsBlock
          title={
            <div
              className="text-primary cursor-pointer trigger flex items-center"
              onClick={() => setShowMore(!showMore)}
            >
              <Translate id={`request_flow.terms.${showMore ? 'show_less_items' : 'show_more_items'}`} />{' '}
              <img
                className={classNames('order-overview--caret', { 'order-overview--caret--up': showMore })}
                src={caret}
              />
            </div>
          }
          borderRadius="top"
          border="bottom"
        />
      )}
    </div>
  );
};

const ItemDisplayProductGroupMaterials: React.FC<{ ps?: ItemProductSelectionTemp }> = ({ ps }) => {
  const lang = getActiveLanguageSettings().labels.full.replace('-', '_');

  const materials = ps?.properties.material_choice?.map(m => m.label?.[lang]).filter(Boolean);
  if (!ps || !materials?.length) {
    return null;
  }

  return <>{materials.join(', ')}</>;
};

const ItemDisplayImage: React.FC<{ image?: string | null }> = ({ image }) => {
  if (image) {
    return <img className="order-overview--image" src={userDataClient.getBaseForIri(image, { h: 160, w: 160 })} />;
  }
  return <div className="order-overview--no-image" />;
};
